import type { MaybeRefOrGetter } from '#imports'
import { toValue, useCoreConfig, useGameConfigurationClient, useQuery } from '#imports'
import type { components } from '@core/types/clients/game-configuration'

export type SectionInfo = {
    slug: string
    name: string
    type: 'Horizontal' | 'Horizontal-Tall' | 'Horizontal-Taller' | 'Vertical'
    horizontalLines: number | null
    horizontalDesktopLines: number | null
    tileDisplayCount: number | null
    containsAllGames: boolean
    position: number | null
    videoEnabled?: boolean
}

/**
 * Fetches the list of active game sections for the current brand.
 * Each section can be passed to `GameCustomSection` to render.
 */
export function useGameSections(options: { enabled?: MaybeRefOrGetter<boolean>, lazy: boolean, server?: boolean, pageSlug?: MaybeRefOrGetter<string | undefined> }) {
    const { client } = useGameConfigurationClient()
    const { site } = useCoreConfig()

    const { enabled = true, pageSlug, lazy, server = true } = options

    return useQuery<SectionInfo[] | undefined>(async ({ signal }) => {
        if (!toValue(enabled)) return undefined

        const slug = toValue(pageSlug)
        const { data } = await client.GET('/public/lists', {
            signal,
            params: {
                query: {
                    brand: site.brand as components['parameters']['brandQueryParameter'],
                    ...(slug && { page_slug: slug })
                },
            },
        })

        const list = data ?? []

        return list
            .filter(item => typeof item.slug === 'string' && item.slug !== '')
            .map((item): SectionInfo => ({
                slug: item.slug as string,
                name: item.name ?? '',
                type: item.type as SectionInfo['type'] ?? 'Horizontal',
                horizontalLines: item.horizontal_lines ?? null,
                horizontalDesktopLines: item.horizontal_desktop_lines ?? item.horizontal_lines ?? null,
                tileDisplayCount: item.tile_display_count ?? null,
                containsAllGames: item.contains_all_games === true,
                position: item.position ?? null,
                videoEnabled: item.video_enabled
            }))
            .sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity))
    }, {
        enabled,
        lazy,
        server,
        // One handler and one result per key: a disabled caller must not share an enabled one's.
        key: () => toValue(enabled)
            ? `game:game-sections:${toValue(pageSlug) ?? ''}`
            : 'game:game-sections:inactive',
    })
}
